import { RFC3339timestamp, UUID } from "@bitory-gmbh/nidavellir";
import { DocumentStateBase } from "@/services/backend/documents/types/state";
import { DocumentOwnership } from "@bitory-gmbh/bsm";
import { v4 } from "uuid";
import t from "@/lang/lang";

export type PricingByArea = {
  id: UUID;
  companyId: UUID;
  branchId: UUID;
  useCalculation: boolean;
  areas: PricingByAreaArea[];
  totalTreatmentFinalPrice: number;
  totalRelativeDiscount: number;
  totalAbsoluteDiscount: number;
  totalDiscountedTreatmentFinalPrice: number;
  totalIncidentalCostTotalAmount: number;
  totalAreaFinalPrice: number;
  createdAt: RFC3339timestamp;
  updatedAt: RFC3339timestamp;
} & DocumentOwnership &
  DocumentStateBase;

export type PricingByAreaArea = {
  id: UUID;
  area: string;
  byAreaId: UUID;
  order: number;
  treatmentFinalPrice: number;
  relativeDiscount: number;
  absoluteDiscount: number;
  discountedTreatmentFinalPrice: number;
  incidentalCostTotalAmount: number;
  areaFinalPrice: number;
};

export function updateUseCalculation(
  byArea: PricingByArea,
  useCalculation: boolean,
): PricingByArea {
  return { ...byArea, useCalculation };
}

// calculateArea recalculates the computed fields of a single area.
// Matches ByAreaArea.calculate() in the Go domain.
export function calculateArea(area: PricingByAreaArea): PricingByAreaArea {
  const discountedTreatmentFinalPrice =
    area.treatmentFinalPrice - area.relativeDiscount - area.absoluteDiscount;
  return {
    ...area,
    discountedTreatmentFinalPrice,
    areaFinalPrice:
      discountedTreatmentFinalPrice + area.incidentalCostTotalAmount,
  };
}

// calculate recalculates all areas and totals of a PricingByArea.
// Matches ByArea.calculate() in the Go domain.
export function calculate(byArea: PricingByArea): PricingByArea {
  const areas = byArea.areas.map(calculateArea);
  return {
    ...byArea,
    areas,
    totalTreatmentFinalPrice: areas.reduce(
      (s, a) => s + a.treatmentFinalPrice,
      0,
    ),
    totalRelativeDiscount: areas.reduce((s, a) => s + a.relativeDiscount, 0),
    totalAbsoluteDiscount: areas.reduce((s, a) => s + a.absoluteDiscount, 0),
    totalDiscountedTreatmentFinalPrice: areas.reduce(
      (s, a) => s + a.discountedTreatmentFinalPrice,
      0,
    ),
    totalIncidentalCostTotalAmount: areas.reduce(
      (s, a) => s + a.incidentalCostTotalAmount,
      0,
    ),
    totalAreaFinalPrice: areas.reduce((s, a) => s + a.areaFinalPrice, 0),
  };
}

// updateArea replaces the area identified by areaId with the updated area,
// recalculates all computed fields and totals.
// Returns the original byArea unchanged if useCalculation is true.
// Matches ByArea.Update() in the Go domain.
export function updateArea(
  byArea: PricingByArea,
  areaId: UUID,
  updated: Partial<PricingByAreaArea>,
): PricingByArea {
  if (byArea.useCalculation) return byArea;
  return calculate({
    ...byArea,
    areas: byArea.areas.map((a) =>
      a.id === areaId ? { ...a, ...updated } : a,
    ),
  });
}

// reorderAreas sets a new area order and keeps totals unchanged.
// Returns the original byArea unchanged if useCalculation is true.
export function reorderAreas(
  byArea: PricingByArea,
  areas: PricingByAreaArea[],
): PricingByArea {
  if (byArea.useCalculation) return byArea;
  return setOrderFieldToIndex({ ...byArea, areas });
}

export function addArea(byArea: PricingByArea, index?: number): PricingByArea {
  const newArea: PricingByAreaArea = {
    id: v4(),
    area: unusedAreaName(byArea, t("Neuer Bereich")),
    byAreaId: byArea.id,
    order: 0,
    treatmentFinalPrice: 0,
    relativeDiscount: 0,
    absoluteDiscount: 0,
    discountedTreatmentFinalPrice: 0,
    incidentalCostTotalAmount: 0,
    areaFinalPrice: 0,
  };

  if (index === undefined || index < 0 || index >= byArea.areas.length) {
    return setOrderFieldToIndex({
      ...byArea,
      areas: [...byArea.areas, newArea],
    });
  }

  const rowsBefore = byArea.areas.slice(0, index);
  const rowsAfter = byArea.areas.slice(index);
  return setOrderFieldToIndex({
    ...byArea,
    areas: [...rowsBefore, newArea, ...rowsAfter],
  });
}

// unusedAreaName returns base, or base with the lowest free counter appended.
// Area names are unique per PricingByArea, so inserting a second row must not
// hand the user a name the backend will reject.
function unusedAreaName(byArea: PricingByArea, base: string): string {
  const taken = new Set(byArea.areas.map((a) => a.area));
  if (!taken.has(base)) return base;

  let counter = 2;
  while (taken.has(`${base} ${counter}`)) counter += 1;
  return `${base} ${counter}`;
}

export function deleteArea(byArea: PricingByArea, areaId: UUID): PricingByArea {
  return setOrderFieldToIndex({
    ...byArea,
    areas: byArea.areas.filter((a) => a.id !== areaId),
  });
}

// setOrderFieldToIndex renumbers the areas to their position in the list.
// Every change to the shape of areas — insert, delete, reorder — has to go
// through it, because the backend requires order to match the position.
export function setOrderFieldToIndex(byArea: PricingByArea): PricingByArea {
  return {
    ...byArea,
    areas: byArea.areas.map((row, index) => ({
      ...row,
      order: index,
    })),
  };
}
